import Skeleton from '@common/components/Skeleton';
import clsx from 'clsx';
import React from 'react';
import RollingPrice from './RollingPrice';

export interface PlanData {
    name: string;
    price: string | number;
    description: string;
    buttonText: string;
    isPopular?: boolean;
    isCustom?: boolean;
    features: string[];
}

interface PricingCardProps {
    plan: PlanData;
    onAction: () => void;
    disabled?: boolean;
    loadingPlanName?: string;
    fetching?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
    plan, onAction, disabled, loadingPlanName, fetching
}) => {
    const isLoading = loadingPlanName === plan.name;
    const isFetching = fetching;
    const isPopular = plan.isPopular;

    // 骨架屏渲染
    if (isFetching) {
        return (
            <div
                className={clsx(
                    'relative rounded-[24px] p-5 flex flex-col border bg-white',
                    'border-gray-200'
                )}
                style={{ width: '350px' }}
            >
                {/* 标题骨架 */}
                <div className="mb-4">
                    <div className="flex items-baseline justify-between mb-1">
                        <Skeleton width="50%" height="20px" />
                    </div>
                    <div className="flex items-baseline gap-1 mb-1 h-9">
                        <Skeleton width="35%" height="30px" />
                        <Skeleton width="15%" height="12px" />
                    </div>
                    <Skeleton width="100%" height="16px" />
                </div>

                {/* 按钮骨架 */}
                <Skeleton width="100%" height="40px" marginBottom="16px" />

                {/* 功能列表骨架 */}
                <ul className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <li key={i} className="flex items-start gap-2 rounded-lg bg-gray-50 p-2">
                            <div className="mt-0.5 w-4 h-4 shrink-0">
                                <Skeleton width="16px" height="16px" />
                            </div>
                            <Skeleton width="100%" height="12px" />
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'relative rounded-[24px] p-5 flex flex-col border transition-all duration-200 bg-white',
                isPopular ? 'border-2 border-text-main shadow-xl scale-[1.01] z-10' : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
            )}
        >
            {isPopular ? (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-text-main text-white text-[10px] font-bold px-3 py-1 rounded-full tracking-wider shadow-md">
                    Most Popular
                </div>
            ) : null}

            <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1">
                    <h3 className="text-lg font-bold text-text-main">
                        {plan.name}
                    </h3>
                </div>

                <div className="flex items-baseline gap-1 mb-1 h-9">
                    {plan.isCustom ? (
                        <span className="text-2xl font-bold text-text-main">
                            Custom
                        </span>
                    ) : (
                        <div className="flex flex-col items-start">
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold text-text-main tracking-tight inline-flex items-center">
                                    $
                                    <RollingPrice price={Number(plan.price)} />
                                </span>
                                <span className="text-text-sub font-medium text-xs">
                                    /mo
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <p className="text-xs text-text-sub min-h-[16px] leading-relaxed line-clamp-1">
                    {plan.description}
                </p>
            </div>

            <button
                onClick={onAction}
                disabled={isLoading || isFetching || disabled}
                className={clsx(
                    'w-full py-2.5 rounded-full font-bold transition-all duration-200 mb-4 text-sm tracking-wide flex items-center justify-center gap-2',
                    isPopular
                        ? 'bg-text-main text-white border-2 border-text-main hover:bg-black hover:border-black shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                        : 'bg-white text-text-main border-2 border-text-main hover:bg-text-main hover:text-white hover:shadow-md hover:-translate-y-0.5',
                    (disabled || isLoading || isFetching) ? 'opacity-50 cursor-not-allowed' : ''
                )}
            >
                {isLoading ? (
                    <>
                        <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                        Processing...
                    </>
                ) : isFetching ? (
                    <>
                        <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                        Loading...
                    </>
                ) : plan.buttonText}
            </button>

            <ul className="space-y-2">
                {(plan.features || []).map((feature, i) => {
                    const isHeader = feature === 'Everything in Basic, plus';
                    return (
                        <li
                            key={i}
                            className={clsx(
                                'flex items-start gap-2 rounded-lg transition-all p-2 bg-gray-50',
                                isHeader && 'mb-1'
                            )}
                        >
                            {!isHeader && (
                                <div className="mt-0.5 w-4 h-4 rounded-full bg-black flex items-center justify-center shrink-0">
                                    <span className="text-white text-[10px] leading-none">✓</span>
                                </div>
                            )}
                            <span className="text-xs leading-relaxed text-gray-700 font-medium">
                                {feature}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PricingCard;
